import { NotebookPen, PhoneIncoming, PhoneOutgoing, Send, type LucideIcon } from 'lucide-react';

/**
 * KPI del día (dueño 2026-10-28): lo que comparten el contador de abajo del logo y la pestaña KPI de
 * Company. El orden es el que pidió: llamadas out, llamadas in, correos y notas.
 * Las definiciones viven en el backend (`modules/kpi/service.ts`); esto es solo cómo se pintan.
 */
export const KPIS = ['calls_out', 'calls_in', 'emails', 'notes'] as const;
export type Kpi = (typeof KPIS)[number];
export type Metas = Record<Kpi, number | null>;
export type Conteos = Record<Kpi, number>;

export const ICONO_KPI: Record<Kpi, LucideIcon> = {
  calls_out: PhoneOutgoing, calls_in: PhoneIncoming, emails: Send, notes: NotebookPen,
};

/** Un tinte por métrica, para reconocerla de un vistazo (sobre el menú navy y sobre la tarjeta). */
export const TINTE_KPI: Record<Kpi, string> = {
  calls_out: '#38bdf8', calls_in: '#818cf8', emails: '#f59e0b', notes: '#2dd4bf',
};

/** La etiqueta larga (i18n) de cada métrica. */
export const ETIQUETA_KPI: Record<Kpi, string> = {
  calls_out: 'kpi.callsOut', calls_in: 'kpi.callsIn', emails: 'kpi.emails', notes: 'kpi.notes',
};
/** La corta, para encabezados de columna. */
export const ETIQUETA_CORTA_KPI: Record<Kpi, string> = {
  calls_out: 'kpi.short.callsOut', calls_in: 'kpi.short.callsIn', emails: 'kpi.short.emails', notes: 'kpi.short.notes',
};

/** ¿Llegó a la meta? Sin meta (null o 0) no hay "llegó": se pinta neutro. */
export function llegoALaMeta(n: number, meta: number | null): boolean | null {
  if (meta == null || meta <= 0) return null;
  return n >= meta;
}

/** Ancho de la barrita de progreso, 0-100. */
export function avance(n: number, meta: number | null): number {
  if (meta == null || meta <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((n / meta) * 100)));
}

/* ───────────── La pestaña KPI (Company) ───────────── */

export type FilaKpiPersona = Conteos & {
  user_id: string; name: string; role: string; department: string | null;
  calls_out_no_answer: number; talk_min: number; days_worked: number;
  days_at_target: Partial<Record<Kpi, number>>;
  daily: Record<Kpi, number[]>; worked: boolean[];
};
export type KpiDelEquipo = {
  from: string; to: string; days: string[]; targets: Metas; rows: FilaKpiPersona[];
  totals: Conteos & { calls_out_no_answer: number; talk_min: number };
};

export const PERIODOS = ['today', 'yesterday', 'week', 'last_week', 'month', 'last_month'] as const;
export type Periodo = (typeof PERIODOS)[number];

/** Hoy en la oficina (America/New_York), como 'YYYY-MM-DD'. */
export function hoyOficina(d: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
}

/** Suma días a una fecha 'YYYY-MM-DD' sin pasar por el huso del navegador (mediodía UTC). */
export function sumarDias(dia: string, n: number): string {
  const d = new Date(`${dia}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** El rango de cada atajo. La semana arranca el LUNES (la semana de trabajo de la oficina). */
export function rangoDelPeriodo(p: Periodo, hoy: string): { from: string; to: string } {
  const dow = (new Date(`${hoy}T12:00:00Z`).getUTCDay() + 6) % 7; // 0 = lunes
  const lunes = sumarDias(hoy, -dow);
  const primero = `${hoy.slice(0, 8)}01`;
  switch (p) {
    case 'today': return { from: hoy, to: hoy };
    case 'yesterday': { const a = sumarDias(hoy, -1); return { from: a, to: a }; }
    case 'week': return { from: lunes, to: hoy };
    case 'last_week': return { from: sumarDias(lunes, -7), to: sumarDias(lunes, -1) };
    case 'month': return { from: primero, to: hoy };
    case 'last_month': { const fin = sumarDias(primero, -1); return { from: `${fin.slice(0, 8)}01`, to: fin }; }
  }
}

export type OrdenKpi = Kpi | 'talk_min' | 'name';

/** Orden del ranking: mayor primero; empate por nombre. Quien no trabajó en el período va al final. */
export function ordenarFilas(rows: FilaKpiPersona[], k: OrdenKpi, dir: 1 | -1): FilaKpiPersona[] {
  return [...rows].sort((a, b) => {
    const sinA = a.days_worked === 0 ? 1 : 0; const sinB = b.days_worked === 0 ? 1 : 0;
    if (sinA !== sinB) return sinA - sinB;
    if (k === 'name') return dir * a.name.localeCompare(b.name);
    const d = (a[k] - b[k]) * dir;
    return d !== 0 ? d : a.name.localeCompare(b.name);
  });
}
